package app.bloomey.web.core.offCanvas.items

import androidx.compose.runtime.*
import app.bloomey.web.components.modals.Toast
import app.bloomey.web.components.widgets.*
import app.bloomey.web.core.data.UiState
import app.bloomey.web.core.data.models.UpdateUserRequest
import app.bloomey.web.core.data.models.User
import app.bloomey.web.core.modal.LocalSiteModelController
import app.bloomey.web.core.modal.modals.DeleteItemModal
import app.bloomey.web.core.offCanvas.LocalOffCanvasController
import app.bloomey.web.core.offCanvas.OffCanvasItem
import app.bloomey.web.core.res.Res
import app.bloomey.web.core.res.icClose
import app.bloomey.web.core.res.icTrash
import app.bloomey.web.modules.users.UsersContract
import app.bloomey.web.modules.users.UsersViewModel
import app.bloomey.web.theme.SpanTextHeader1Variant
import app.bloomey.web.theme.toSitePalette
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.coroutines.flow.collectLatest
import kotlinx.datetime.Instant
import kotlinx.datetime.toJSDate
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Text

class UserOffCanvas(private val userId: String, private val user: User? = null, val onModified: () -> Unit = {}) :
    OffCanvasItem {
    override val pathId: String
        get() {
            return "user:$userId"
        }

    @Composable
    override fun Content(modifier: Modifier) {
        val sitePalette = ColorMode.current.toSitePalette()
        val offCanvasController = LocalOffCanvasController.current
        val siteModalController = LocalSiteModelController.current
        val scope = rememberCoroutineScope()
        val viewModel = remember { UsersViewModel(scope) }
        val uiState by viewModel.observeStates().collectAsState()

        var firstName by remember { mutableStateOf(user?.firstName ?: "") }
        var lastName by remember { mutableStateOf(user?.lastName ?: "") }
        var email by remember { mutableStateOf(user?.email ?: "") }
        var blackListed by remember { mutableStateOf(user?.blackListed ?: false) }

        val getUser = {
            viewModel.trySend(UsersContract.Inputs.GetUser(userId))
        }
        LaunchedEffect(Unit) {
            if (user == null) {
                getUser()
            }
            viewModel.observeStates().collectLatest {
                when {
                    it.updateUser.isSuccess || it.deleteUser.isSuccess -> {
                        onModified()
                        offCanvasController.pop()
                    }

                    it.getUser.isSuccess -> {
                        val user: User = it.getUser.getData
                        firstName = user.firstName
                        lastName = user.lastName
                        email = user.email
                        blackListed = user.blackListed
                    }
                }
            }
        }
        Column(
            modifier = modifier
                .padding(40.px)
                .background(sitePalette.background)
        ) {
            Row(modifier = Modifier.fillMaxWidth()) {
            SvgLoader(
                Res.Assets.icClose,
                modifier = Modifier.size(24.px)
                    .cursor(Cursor.Pointer)
                    .onClick {
                        offCanvasController.pop()
                    },
                color = sitePalette.onBackgroundLight
            )
            Spacer()
            if (uiState.deleteUser.isLoading)
                LoadingView(size = 24.px)
            else
                SvgLoader(
                    Res.Assets.icTrash,
                    modifier = Modifier.size(24.px)
                        .cursor(Cursor.Pointer)
                        .onClick {
                            siteModalController.push(
                                DeleteItemModal(
                                    title = "Delete User",
                                    text = "Are you sure you want to delete this user? This will delete all their data created on the system",
                                    onDelete = {
                                        val input = UsersContract.Inputs.DeleteUser(userId)
                                        viewModel.trySend(input)
                                    }
                                )
                            )
                        },
                    color = Colors.Red
                )
        }
            Height(20.px)
            SpanText("User", variant = SpanTextHeader1Variant)
            Height(4.px)
            SpanText("#$userId")
            user?.let {
                Height(4.px)
                Row(modifier = Modifier.flexWrap(FlexWrap.Wrap)) {
                    SpanText("Joined ", modifier = Modifier.fontWeight(FontWeight.SemiBold))
                    SpanText(
                        Instant.fromEpochMilliseconds(user.createdAt)
                            .toJSDate().let {
                                "${it.toDateString()} ${it.toLocaleTimeString()}"
                            }
                    )
                }
            }
            Height(16.percent)
            when {
                uiState.getUser.isSuccess || user != null -> {

                    Column(modifier = Modifier.gap(20.px).fillMaxWidth()) {
                        SiteTextInput(
                            label = "First Name",
                            text = firstName,
                            modifier = Modifier.fillMaxWidth(),
                            required = true
                        ) {
                            firstName = it
                        }
                        SiteTextInput(
                            label = "Last Name",
                            text = lastName,
                            modifier = Modifier.fillMaxWidth(),
                            required = true
                        ) {
                            lastName = it
                        }
                        SiteTextInput(
                            label = "Email",
                            text = email,
                            modifier = Modifier.fillMaxWidth(),
                            enabled = false,
                            required = true
                        ) {
                            email = it
                        }
                        SwitchTile(text = "BlackListed", checked = blackListed) {
                            blackListed = it
                        }
                    }
                    Spacer()
                    SiteButton(
                        modifier = Modifier.fillMaxWidth(),
                        loading = uiState.updateUser.isLoading,
                        onClick = {
                            viewModel.trySend(
                                UsersContract.Inputs.UpdateUser(
                                    userId, request = UpdateUserRequest(
                                        firstName = firstName,
                                        lastName = lastName,
                                        blackListed = blackListed
                                    )
                                )
                            )
                        }) {
                        Text("Update")
                    }

                    Toast(message = uiState.updateUser.getMessage ?: "", show = uiState.updateUser.isError) {

                    }
                }

                else -> {
                    when (val state = uiState.getUser) {
                        is UiState.Error -> SiteErrorView(
                            modifier = Modifier.fillMaxSize(),
                            text = state.errorMessage
                        ) {
                            getUser()
                        }

                        UiState.Initial -> {}
                        UiState.Loading -> LoadingView()
                        is UiState.Success -> {}
                    }
                }
            }
        }
    }
}